import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useProjects } from '../hooks/useProject';
import { useTemplates } from '../hooks/useTemplates';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const NewProjectPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { createProject, loading } = useProjects();
  const { templates } = useTemplates();

  const [formData, setFormData] = useState({
    name: '',
    author: '',
    description: '',
    category: 'custom',
    template_id: null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.author.trim()) {
      toast.error(t('common.error'), {
        description: 'Nombre y autor son obligatorios',
      });
      return;
    }

    try {
      const project = await createProject(formData);
      toast.success(t('common.success'), {
        description: 'Proyecto creado',
      });
      navigate(`/editor/${project.id}`);
    } catch (error) {
      toast.error(t('common.error'), {
        description: error.message || 'Error al crear el proyecto',
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-white" data-testid="new-project-page">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('new_project.title')}</h1>
          <p className="text-gray-600">{t('new_project.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nombre */}
          <div>
            <Label htmlFor="name" className="text-sm font-medium text-gray-900 mb-1.5 block">
              {t('new_project.name')} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('new_project.name_placeholder')}
              className="border-gray-200 focus:border-blue-500 focus:ring-blue-500"
              data-testid="project-name-input"
            />
          </div>

          {/* Autor */}
          <div>
            <Label htmlFor="author" className="text-sm font-medium text-gray-900 mb-1.5 block">
              {t('new_project.author')} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) => handleChange('author', e.target.value)}
              placeholder={t('new_project.author_placeholder')}
              className="border-gray-200 focus:border-blue-500 focus:ring-blue-500"
              data-testid="project-author-input"
            />
          </div>

          {/* Descripción */}
          <div>
            <Label htmlFor="description" className="text-sm font-medium text-gray-900 mb-1.5 block">
              {t('new_project.description')}
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('new_project.description_placeholder')}
              className="border-gray-200 focus:border-blue-500 focus:ring-blue-500 min-h-[80px] resize-none"
              data-testid="project-description-input"
            />
          </div>

          {/* Categoría & Plantilla */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category" className="text-sm font-medium text-gray-900 mb-1.5 block">
                {t('new_project.category')}
              </Label>
              <Select value={formData.category} onValueChange={(val) => handleChange('category', val)}>
                <SelectTrigger className="border-gray-200" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">{t('new_project.category_custom')}</SelectItem>
                  <SelectItem value="mob">{t('new_project.category_mob')}</SelectItem>
                  <SelectItem value="npc">{t('new_project.category_npc')}</SelectItem>
                  <SelectItem value="monster">{t('new_project.category_monster')}</SelectItem>
                  <SelectItem value="animal">{t('new_project.category_animal')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="template" className="text-sm font-medium text-gray-900 mb-1.5 block">
                {t('new_project.select_template')}
              </Label>
              <Select
                value={formData.template_id || 'none'}
                onValueChange={(val) => handleChange('template_id', val === 'none' ? null : val)}
              >
                <SelectTrigger className="border-gray-200" data-testid="template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('new_project.no_template')}</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="create-project-button"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('new_project.create')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/')}
              className="border-gray-200"
              data-testid="cancel-button"
            >
              {t('new_project.cancel')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default NewProjectPage;
